import i18next from 'i18next'
import type { TicketResponse } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'

export const OPEN_STATUSES = ['NEW', 'OPEN', 'IN_PROGRESS']
export const MERGED_STATUS = 'MERGED'
export const CLOSED_STATUSES = ['RESOLVED', 'CLOSED', 'INVALID', MERGED_STATUS]

export const ALLOWED_ATTACHMENT_TYPES = ['.pdf', '.png', '.jpg', '.csv']
export const ALLOWED_ATTACHMENT_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'text/csv']
export const MAX_ATTACHMENT_SIZE_MB = 10
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024
export const MAX_MESSAGE_LENGTH = 10000
export const MAX_INITIAL_MESSAGES = 10
// Derived, not a separate BE-enforced limit -- already guaranteed by the two caps above.
// Shown as a running total purely for UX transparency.
export const MAX_TOTAL_MESSAGE_LENGTH = MAX_MESSAGE_LENGTH * MAX_INITIAL_MESSAGES
export const TICKETS_PAGE_SIZE = 9

export function formatMessageTime(dateStr?: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  const month = d.toLocaleDateString(i18next.resolvedLanguage || i18next.language || 'en', { month: 'short' })
  return `${h}:${m} · ${d.getDate()} ${month}`
}

export function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return ''
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diffMs / 86400000)
  const hours = Math.floor(diffMs / 3600000)
  const mins = Math.floor(diffMs / 60000)
  if (days > 0) return tr('FRONTEND.SUPPORT.TIME.DAYS_AGO', '{{count}}d ago', { count: days })
  if (hours > 0) return tr('FRONTEND.SUPPORT.TIME.HOURS_AGO', '{{count}}h ago', { count: hours })
  if (mins > 0) return tr('FRONTEND.SUPPORT.TIME.MINUTES_AGO', '{{count}}m ago', { count: mins })
  return tr('FRONTEND.SUPPORT.TIME.JUST_NOW', 'just now')
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(i18next.resolvedLanguage || i18next.language || 'en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export const INVALID_STATUS = 'INVALID'

export function isTicketOpen(status?: string): boolean {
  return OPEN_STATUSES.includes(status ?? '')
}

export function canReopenTicket(status?: string): boolean {
  // Reopen allowed for any closed status except INVALID or MERGED
  return !isTicketOpen(status) && status !== INVALID_STATUS && status !== MERGED_STATUS
}

export function isTicketInvalid(status?: string): boolean {
  return status === INVALID_STATUS
}

export function isTicketMerged(status?: string): boolean {
  return status === MERGED_STATUS
}

export function getMergedIntoId(ticket?: TicketResponse): number | undefined {
  return ticket?.mergedIntoId
}

export function getMergedIds(ticket?: TicketResponse): number[] {
  return ticket?.mergedIds ?? []
}

export function getDisplayStatus(status?: string): 'open' | 'closed' {
  return isTicketOpen(status) ? 'open' : 'closed'
}

export interface FileValidationResult {
  valid: File[]
  errors: string[]
}

export function validateFiles(files: File[]): FileValidationResult {
  const valid: File[] = []
  const errors: string[] = []

  for (const file of files) {
    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`
    if (!ALLOWED_ATTACHMENT_TYPES.includes(ext)) {
      errors.push(`${file.name}: unsupported file type (allowed: ${ALLOWED_ATTACHMENT_TYPES.join(', ')})`)
    } else if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      errors.push(`${file.name}: file exceeds ${MAX_ATTACHMENT_SIZE_MB}MB limit`)
    } else {
      valid.push(file)
    }
  }

  return { valid, errors }
}

export function callRequestTitle(pending?: boolean): string {
  return pending
    ? tr('FRONTEND.CALL_REQUEST.FAB.TITLE_PENDING', 'Request already pending')
    : tr('FRONTEND.CALL_REQUEST.FAB.TITLE', 'Request a call')
}
